import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart, Award, Globe, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { aboutData, contactData } from "@/data/mock";

const valueIcons = [Target, Heart, Award, Users];

export default function About() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-cyan-500/5 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About <span className="text-cyan-400">Botverse Web</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              {aboutData.story}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-cyan-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
                <p className="text-slate-400 leading-relaxed text-lg">
                  {aboutData.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-teal-500/10 flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-teal-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
                <p className="text-slate-400 leading-relaxed text-lg">
                  {aboutData.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Our Values</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.values.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <div key={index} className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700 hover:border-cyan-500/30 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-6">
                <Globe className="w-5 h-5 text-cyan-400" />
                <span className="text-cyan-400 font-medium">Global Presence</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">Operating Across Continents</h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                Botverse Web connects talent and businesses worldwide, operating across multiple continents to deliver world-class AI solutions and education.
              </p>
              <div className="flex flex-wrap gap-3">
                {contactData.regions.map((region, index) => (
                  <span key={index} className="px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-sm">
                    {region}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-3xl blur-xl" />
              <div className="relative grid grid-cols-2 gap-4">
                <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">5</div>
                  <div className="text-slate-400 text-sm">Continents</div>
                </div>
                <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">20+</div>
                  <div className="text-slate-400 text-sm">Countries</div>
                </div>
                <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">50+</div>
                  <div className="text-slate-400 text-sm">Projects</div>
                </div>
                <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">200+</div>
                  <div className="text-slate-400 text-sm">Alumni</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Work With Us</h2>
            <p className="text-slate-400 mb-8">
              Whether you're looking to implement AI in your business or advance your career in tech, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/consultation">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold px-8 group">
                  Book a Consultation
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
